import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Twitter, Instagram, Linkedin, Mail, Phone, MapPin, ExternalLink, Youtube } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Footer = () => {
  const { t, i18n } = useTranslation();
  return (
    <footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
          
          {/* Dekanlık Section */}
          <div className="space-y-6">
            <div className="flex items-center space-x-3">
              <div className="w-14 h-14 bg-gradient-to-br from-hacettepe-blue to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-white font-bold text-2xl">H</span>
              </div>
              <div>
                <div className="font-bold text-xl text-white">{t('footer.deanOffice')}</div>
                <div className="text-hacettepe-red text-sm font-medium">{t('footer.facultyFullName')}</div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <MapPin size={18} className="text-hacettepe-red mt-1 flex-shrink-0" />
                <div className="text-gray-300 text-sm leading-relaxed">
                  {t('footer.address')}
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center space-x-3">
                  <Phone size={16} className="text-hacettepe-red flex-shrink-0" />
                  <span className="text-gray-300 text-sm">(+90312) 299 20 80</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone size={16} className="text-hacettepe-red flex-shrink-0" />
                  <span className="text-gray-300 text-sm">(+90312) 297 68 50</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone size={16} className="text-hacettepe-red flex-shrink-0" />
                  <span className="text-gray-300 text-sm">(+90312) 297 68 51</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone size={16} className="text-hacettepe-red flex-shrink-0" />
                  <span className="text-gray-300 text-sm">(+90312) 299 20 93</span>
                </div>
              </div>
              
              <a
                href="mailto:fendek@hacettepe.edu.tr"
                className="flex items-center space-x-3 hover:text-hacettepe-red transition-colors group"
              >
                <Mail size={16} className="text-hacettepe-red flex-shrink-0 group-hover:scale-110 transition-transform" />
                <span className="text-gray-300 text-sm group-hover:text-hacettepe-red">{t('footer.contactUs')}</span>
              </a>
            </div>
          </div>

          {/* Bölümler Section */}
          <div className="space-y-6">
            <h3 className="font-bold text-xl text-white border-b-2 border-hacettepe-red pb-2 inline-block">
              {t('footer.departments')}
            </h3>
            
            <div className="space-y-4">
              <div className="group">
                <a 
                  href="https://aktuerya.hacettepe.edu.tr/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg hover:bg-gray-700/70 transition-all duration-300 group-hover:shadow-lg"
                >
                  <div>
                    <div className="font-semibold text-white group-hover:text-hacettepe-red transition-colors">
                      {t('footer.actuarialScience')}
                    </div>
                    <div className="text-gray-400 text-sm">(+90312) 297 79 98</div>
                  </div>
                  <ExternalLink size={16} className="text-gray-400 group-hover:text-hacettepe-red transition-colors" />
                </a>
              </div>
              
              <div className="group">
                <a 
                  href="https://biology.hacettepe.edu.tr/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg hover:bg-gray-700/70 transition-all duration-300 group-hover:shadow-lg"
                >
                  <div>
                    <div className="font-semibold text-white group-hover:text-hacettepe-red transition-colors">
                      {t('footer.biology')}
                    </div>
                    <div className="text-gray-400 text-sm">(+90312) 297 80 00</div>
                  </div>
                  <ExternalLink size={16} className="text-gray-400 group-hover:text-hacettepe-red transition-colors" />
                </a>
              </div>
              
              <div className="group">
                <a 
                  href="https://stat.hacettepe.edu.tr/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg hover:bg-gray-700/70 transition-all duration-300 group-hover:shadow-lg"
                >
                  <div>
                    <div className="font-semibold text-white group-hover:text-hacettepe-red transition-colors">
                      {t('footer.statistics')}
                    </div>
                    <div className="text-gray-400 text-sm">(+90312) 297 79 00</div>
                  </div>
                  <ExternalLink size={16} className="text-gray-400 group-hover:text-hacettepe-red transition-colors" />
                </a>
              </div>
              
              <div className="group">
                <a 
                  href="https://chem.hacettepe.edu.tr/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg hover:bg-gray-700/70 transition-all duration-300 group-hover:shadow-lg"
                >
                  <div>
                    <div className="font-semibold text-white group-hover:text-hacettepe-red transition-colors">
                      {t('footer.chemistry')}
                    </div>
                    <div className="text-gray-400 text-sm">(+90312) 297 79 40</div>
                  </div>
                  <ExternalLink size={16} className="text-gray-400 group-hover:text-hacettepe-red transition-colors" />
                </a>
              </div>
              
              <div className="group">
                <a 
                  href="https://mat.hacettepe.edu.tr/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg hover:bg-gray-700/70 transition-all duration-300 group-hover:shadow-lg"
                >
                  <div>
                    <div className="font-semibold text-white group-hover:text-hacettepe-red transition-colors">
                      {t('footer.mathematics')}
                    </div>
                    <div className="text-gray-400 text-sm">(+90312) 297 78 50</div>
                  </div>
                  <ExternalLink size={16} className="text-gray-400 group-hover:text-hacettepe-red transition-colors" />
                </a>
              </div>
            </div>
          </div>

          {/* Social Media Section */}
          <div className="space-y-6">
            <h3 className="font-bold text-xl text-white border-b-2 border-hacettepe-red pb-2 inline-block">
              {t('footer.followUs')}
            </h3>
            
            <div className="flex space-x-4">
              <a 
                href="https://www.instagram.com/hacettepefenfakultesi" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-12 h-12 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full flex items-center justify-center hover:from-pink-600 hover:to-purple-700 transition-all duration-300 transform hover:scale-110 shadow-lg"
              >
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              
              <a 
                href="https://x.com/hacettepefenfak" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center hover:from-blue-600 hover:to-blue-700 transition-all duration-300 transform hover:scale-110 shadow-lg"
              >
                <Twitter size={20} className="text-white" />
              </a>
              
              <a 
                href="https://www.youtube.com/@hacettepeuniversitesifenfa9147" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-12 h-12 bg-gradient-to-r from-red-500 to-red-600 rounded-full flex items-center justify-center hover:from-red-600 hover:to-red-700 transition-all duration-300 transform hover:scale-110 shadow-lg"
              >
                <Youtube size={20} className="text-white" />
              </a>
            </div>
            

          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-gray-700 bg-gray-900/50">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-gray-300 text-sm">
              © 2024 {t('footer.facultyFullName')}. {t('footer.allRightsReserved')}
            </div>
            <div className="flex items-center space-x-6 text-sm">
              <a href="https://www.hacettepe.edu.tr/gizlilik" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-hacettepe-red transition-colors">
                {t('footer.privacyPolicy')}
              </a>
              <a href="https://www.hacettepe.edu.tr/kullanim-kosullari" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-hacettepe-red transition-colors">
                {t('footer.termsOfUse')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;